import { BrandMediaService } from '@inference.engine/guidelines-api-client/web';
import { guidelinesHttp } from '@/lib/api/guidelines-client';
import { describeApiFailure } from '@/shared/api/describeApiFailure';

/** A folder in the workspace's media tree, as the app reads it. */
export interface MediaFolder {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  /**
   * Media and fonts filed directly here, as one number: one tree holds both, and a folder with a
   * campaign's photography and its typeface in it holds three things rather than two and one.
   * Not a rollup, so a parent does not claim what its children hold.
   */
  itemCount: number;
  /** Filed here and anywhere beneath it in the tree. What a rail shows, since selecting a folder is about to mean its whole subtree. */
  subtreeCount: number;
}

export interface MediaFolderTree {
  folders: MediaFolder[];
  /** Media and fonts in no folder at all. A count rather than a folder, because it has no id to name. */
  unfiled: number;
}

/** The route's own maximum, so a whole tree costs as few round trips as it can. */
const FOLDER_PAGE_SIZE = 200;

/** Stops a server that always answers `hasMore` from looping until the tab dies. */
const MAX_FOLDER_PAGES = 25;

/**
 * The whole tree, following the cursor.
 *
 * Hand mirrored for the same reason the media list is: these endpoints declare no response schema,
 * so the shapes never reach the OpenAPI spec and the calls below cast.
 *
 * Paging is not optional here the way it is for a grid. The rail draws a tree from parent ids, so a
 * page that ends mid-tree does not show a short list -- it drops every child of a parent that fell
 * off the end, and those children are then unreachable rather than merely further down.
 */
export const listMediaFolders = async (): Promise<MediaFolderTree> => {
  const folders: MediaFolder[] = [];
  let unfiled = 0;
  let cursor: string | undefined;

  for (let page = 0; page < MAX_FOLDER_PAGES; page += 1) {
    const response = await BrandMediaService.folders({
      client: guidelinesHttp,
      query: { pageSize: FOLDER_PAGE_SIZE, ...(cursor === undefined ? {} : { cursor }) },
    });

    if (!response.data) {
      throw new Error(describeApiFailure(response.error, 'The folders could not be loaded.'));
    }

    const body = response.data as unknown as
      | (Partial<MediaFolderTree> & { nextCursor?: string | null; hasMore?: boolean })
      | null;
    if (!Array.isArray(body?.folders)) {
      throw new Error('The folders could not be loaded.');
    }

    folders.push(...body.folders);
    // Counted over the workspace rather than the page, so the last answer is as good as the first.
    unfiled = body.unfiled ?? unfiled;
    if (!body.hasMore || !body.nextCursor) break;
    cursor = body.nextCursor;
  }

  return { folders, unfiled };
};

export const createMediaFolder = async (input: {
  name: string;
  parentId?: string;
  owner?: 'content_board';
}): Promise<MediaFolder> => {
  const response = await BrandMediaService.createFolder({ client: guidelinesHttp, body: input });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The folder could not be created.'));
  }

  return response.data as unknown as MediaFolder;
};

/**
 * Files media and fonts into a folder, or out of every folder when `folderId` is null.
 *
 * Null is sent rather than omitted: leaving the field out means "no instruction", and taking an
 * asset out of its folder is an instruction.
 *
 * Both kinds in one call rather than one call each, because a person selecting across the library
 * made one move: two requests could half-succeed, leaving a selection split across two folders.
 * Either list may be omitted; naming neither is refused rather than answered as a successful
 * nothing.
 */
export const fileMediaIntoFolder = async (input: {
  mediaIds?: string[];
  fontIds?: string[];
  folderId: string | null;
}): Promise<{ filed: number }> => {
  const response = await BrandMediaService.fileIntoFolder({ client: guidelinesHttp, body: input });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The media could not be filed.'));
  }

  return response.data as unknown as { filed: number };
};

/**
 * Renames a folder. The slug is the server's to keep: it is how a folder is addressed without
 * walking its ancestors, so a rename moves the label and nothing else.
 */
export const renameMediaFolder = async (input: {
  folderId: string;
  name: string;
}): Promise<{ renamed: boolean }> => {
  const response = await BrandMediaService.renameFolder({
    client: guidelinesHttp,
    path: { folderId: input.folderId },
    body: { name: input.name },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The folder could not be renamed.'));
  }

  return response.data as unknown as { renamed: boolean };
};

/**
 * Deletes a folder, the folders beneath it, and retires the media and fonts filed anywhere in that
 * subtree.
 *
 * The counts come back rather than a bare acknowledgement: the folder rows and what was filed in
 * them are two different kinds of loss, and a person who just confirmed one needs to be told what
 * actually went.
 */
export const deleteMediaFolder = async (input: {
  folderId: string;
}): Promise<DeleteFolderResult> => {
  const response = await BrandMediaService.deleteFolder({
    client: guidelinesHttp,
    path: { folderId: input.folderId },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The folder could not be deleted.'));
  }

  return response.data as unknown as DeleteFolderResult;
};

/** Media and fonts counted apart, because a person is owed the specifics of what they just lost. */
export interface DeleteFolderResult {
  deletedFolders: number;
  archivedMedia: number;
  archivedFonts: number;
}
